import { useState, useEffect, useMemo, useContext } from 'react';
import apiUser from '../../apiUser';
import { UserContext } from '../../contexts/userProvider';
import LoadingSpinner from '../Loader/load';
import Modal from './Modal';
import { avisoDe, UFS, nomeContrato, ContratoStatusBadge } from './format';

// clientes.estado guarda CÓDIGO IBGE; mapeia p/ a sigla exibida e p/ o filtro.
const UF_COD = { RS: '43', SC: '42', PR: '41' };
const COD_UF = { 43: 'RS', 42: 'SC', 41: 'PR', 16: 'AP', 35: 'SP', 50: 'MS' };
const ufSigla = (cod) => COD_UF[Number(cod)] || (cod ? String(cod) : '—');

// Lista de CONTRATOS (Leva 2B: o eixo do faturamento é o contrato, não mais o
// cliente — um cliente pode ter N contratos, cada um com sua própria ativação) com
// switch ativado/desativado do faturamento + modal de confirmação.
// Ativar = o contrato passa a receber o relatório de cobrança no disparo, então a troca é confirmada.
const ClientesAtivacao = () => {
    const { setAlert } = useContext(UserContext);
    const [contratos, setContratos] = useState(null);
    const [busca, setBusca] = useState('');
    const [estado, setEstado] = useState('');
    const [soAtivos, setSoAtivos] = useState(false);
    const [confirm, setConfirm] = useState(null);   // { contrato, novo }
    const [salvando, setSalvando] = useState(false);
    const aviso = avisoDe(setAlert);

    const carregar = async () => {
        try {
            const { data } = await apiUser.get('/faturamento/ativacao');
            setContratos(data.success ? (data.dados || []) : []);
            if (!data.success) aviso(data.message);
        } catch { aviso('Erro ao carregar contratos.'); setContratos([]); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { carregar(); }, []);

    const confirmarToggle = async () => {
        const { contrato, novo } = confirm;
        setSalvando(true);
        try {
            const { data } = await apiUser.post('/faturamento/contratoHabilitado', { id_contrato: contrato.id, habilitado: novo });
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) setContratos((prev) => prev.map((c) => (c.id === contrato.id ? { ...c, habilitado: novo ? '1' : '0' } : c)));
        } catch { aviso('Erro ao comunicar com o servidor.'); }
        setSalvando(false); setConfirm(null);
    };

    const estadoMatch = (c) => {
        if (!estado) return true;
        if (estado === 'outros') return !['43', '42', '41'].includes(String(c.estado));
        return String(c.estado) === UF_COD[estado];
    };

    const linhas = useMemo(() => {
        if (!contratos) return [];
        const q = busca.trim().toLowerCase();
        return contratos.filter((c) =>
            (!q || (c.cliente_nome || '').toLowerCase().includes(q) || (c.nome || '').toLowerCase().includes(q) || String(c.numero || '').toLowerCase().includes(q)) &&
            estadoMatch(c) &&
            (!soAtivos || c.habilitado === '1'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contratos, busca, estado, soAtivos]);

    if (!contratos) return <LoadingSpinner />;
    const ativos = contratos.filter((c) => c.habilitado === '1').length;

    return (
        <div className='w-full'>
            <div className='flex items-end gap-3 flex-wrap mb-4'>
                <div className='px-4 py-2 rounded-lg bg-green-50 text-green-900 border border-green-200 text-sm font-semibold'>
                    {ativos} de {contratos.length} ativos
                </div>
                <div>
                    <label className='block text-xs text-gray-600 dark:text-gray-300 mb-1'>Buscar cliente ou contrato</label>
                    <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder='Nome'
                        className='border rounded-lg p-2 w-56 dark:bg-gray-700 dark:text-white' />
                </div>
                <div>
                    <label className='block text-xs text-gray-600 dark:text-gray-300 mb-1'>Estado</label>
                    <select value={estado} onChange={(e) => setEstado(e.target.value)} className='border rounded-lg p-2 dark:bg-gray-700 dark:text-white'>
                        <option value=''>Todos</option>
                        {UFS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </div>
                <label className='flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 pb-2 cursor-pointer select-none'>
                    <input type='checkbox' checked={soAtivos} onChange={(e) => setSoAtivos(e.target.checked)} className='w-4 h-4 accent-vox-blue-600' />
                    Só ativos
                </label>
            </div>

            <div className='rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden'>
                <table className='w-full text-sm'>
                    <thead>
                        <tr className='bg-gradient-to-r from-vox-blue-600 to-vox-blue-800 text-white text-left'>
                            <th className='px-4 py-3 font-medium uppercase tracking-wider text-xs'>Cliente</th>
                            <th className='px-4 py-3 font-medium uppercase tracking-wider text-xs'>Contrato</th>
                            <th className='px-4 py-3 w-28 text-center font-medium uppercase tracking-wider text-xs'>Status do contrato</th>
                            <th className='px-4 py-3 w-16 text-center font-medium uppercase tracking-wider text-xs'>UF</th>
                            <th className='px-4 py-3 w-28 text-center font-medium uppercase tracking-wider text-xs'>E-mails</th>
                            <th className='px-4 py-3 font-medium uppercase tracking-wider text-xs'>Remetente</th>
                            <th className='px-4 py-3 w-32 text-center font-medium uppercase tracking-wider text-xs'>Faturamento</th>
                        </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-200 dark:divide-gray-700'>
                        {linhas.map((c) => {
                            const on = c.habilitado === '1';
                            const semDest = Number(c.qtd_para || 0) === 0;
                            const semValorGlobal = c.tipo === 'global' && !(Number(c.valor) > 0);
                            const usaSpxo = Number(c.tem_item_spxo) === 1;
                            return (
                                <tr key={c.id} className='dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'>
                                    <td className='px-4 py-2.5'>{c.cliente_nome}</td>
                                    <td className='px-4 py-2.5'>
                                        {nomeContrato(c)}
                                        {on && semDest && <span className='ml-2 text-xs text-yellow-700 dark:text-yellow-400'>⚠ sem destinatário</span>}
                                        {on && semValorGlobal && <span className='ml-2 text-xs text-yellow-700 dark:text-yellow-400' title='Contrato global sem Valor Global: não dispara e-mail até definir (aba Contratos do cliente)'>⚠ sem valor global</span>}
                                        {usaSpxo && <span className='ml-2 text-xs text-blue-700 dark:text-blue-300' title='Contrato com item integrado ao SPXO: o disparo sincroniza e exige dados da competência'>⚡ SPXO</span>}
                                        {c.status === 'implantacao' && <span className='ml-2 text-xs text-sky-700 dark:text-sky-300' title='Contrato em implantação: só entra no faturamento após a Homologação da Instalação (Eventos do contrato)'>⏳ em implantação</span>}
                                    </td>
                                    <td className='px-4 py-2.5 text-center'><ContratoStatusBadge status={c.status} compact /></td>
                                    <td className='px-4 py-2.5 text-center text-gray-500 dark:text-gray-400'>{ufSigla(c.estado)}</td>
                                    <td className='px-4 py-2.5 text-center'>{Number(c.qtd_para || 0)}{Number(c.qtd_cc || 0) ? ` +${c.qtd_cc}cc` : ''}</td>
                                    <td className='px-4 py-2.5 text-gray-500 dark:text-gray-400'>{c.remetente_nome || 'Padrão'}</td>
                                    <td className='px-4 py-2.5 text-center'>
                                        <button type='button' onClick={() => setConfirm({ contrato: c, novo: !on })}
                                            title={on ? 'Desativar faturamento' : 'Ativar faturamento'}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {linhas.length === 0 && (
                            <tr><td colSpan={7} className='p-6 text-center text-gray-500'>Nenhum contrato neste filtro.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {confirm && (
                <Modal
                    maxW='max-w-md'
                    title={confirm.novo ? 'Ativar faturamento?' : 'Desativar faturamento?'}
                    onClose={() => (salvando ? null : setConfirm(null))}
                    footer={
                        <>
                            <button onClick={() => setConfirm(null)} disabled={salvando}
                                className='px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-white rounded-lg disabled:opacity-50'>Cancelar</button>
                            <button onClick={confirmarToggle} disabled={salvando}
                                className={`px-4 py-2 rounded-lg text-white font-semibold disabled:opacity-50 ${confirm.novo ? '' : 'bg-red-500 hover:bg-red-600'}`}
                                style={confirm.novo ? { backgroundColor: '#9ac31c' } : {}}>
                                {salvando ? 'Salvando...' : (confirm.novo ? 'Ativar' : 'Desativar')}
                            </button>
                        </>
                    }
                >
                    <div className='px-6 py-5 text-sm text-gray-700 dark:text-gray-200 space-y-3'>
                        <p className='font-semibold'>{confirm.contrato.cliente_nome} — {nomeContrato(confirm.contrato)}</p>
                        {confirm.novo ? (
                            <>
                                <p>Ao ativar, este contrato passa a <b>receber o relatório de cobrança por e-mail</b> no próximo disparo (automático ou manual).</p>
                                {Number(confirm.contrato.qtd_para || 0) === 0 && (
                                    <p className='bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-yellow-900'>
                                        ⚠ Este contrato <b>não tem destinatário cadastrado</b> — o envio vai falhar. Cadastre o e-mail em <b>Destinatários</b> antes de ativar.
                                    </p>
                                )}
                                {confirm.contrato.tipo === 'global' && !(Number(confirm.contrato.valor) > 0) && (
                                    <p className='bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-yellow-900'>
                                        ⚠ Este contrato global <b>não tem Valor Global definido</b> — mesmo habilitado, NÃO dispara até definir (aba Contratos do cliente).
                                    </p>
                                )}
                            </>
                        ) : (
                            <p>Ao desativar, este contrato <b>deixa de receber</b> o relatório no disparo. Os e-mails cadastrados continuam guardados.</p>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ClientesAtivacao;
